// App shell — Smokeball design-system chrome (see design/design-system.css,
// generated from the "Internal AI platform design system" claude.ai/design
// project): white header with the Smokeball logo + "AI" wordmark and the
// orange global Create New, white sidebar with Platform / Build nav groups
// (cyan active item), and the `.page` main slot.
//
// **Navigation routes** are stubbed against the routes registered in
// `app.tsx`. `PrimaryNav` stays pure + prop-driven (previewEnabled +
// pendingBadge slot) so it unit-tests behind a `MemoryRouter` alone.

import { Link, NavLink, Outlet } from 'react-router-dom';

import smokeballLogo from '../design/logo/smokeball-default.png';
import { SbIcon } from '../design/sb-icon.js';
import { usePreviewFeatures } from '../prefs/index.js';
import { usePendingApprovalsCount } from './pending-approvals-badge.js';
import { UserMenu } from './user-menu.js';
import { VersionToast } from './version-toast.js';

export interface AppLayoutProps {
  /**
   * Optional banner element rendered above the routed page content. Used by
   * the error-boundary and the "session refreshing..." status notice. Most
   * renders pass nothing.
   */
  readonly banner?: React.ReactNode;
}

export interface PrimaryNavProps {
  /**
   * Whether preview features are visible. Gates the in-progress nav items
   * (Inbox, Connectors, Preview, Archie). COSMETIC nav-hiding only: the
   * routes stay registered + reachable by URL, and server-side authorization
   * is unchanged.
   */
  readonly previewEnabled: boolean;
  /**
   * Slot for the pending-approvals badge rendered inside the Inbox link.
   * Passed as a prop (not imported here) so this view stays prop-driven and
   * unit-testable WITHOUT a tRPC provider.
   */
  readonly pendingBadge?: React.ReactNode;
}

/**
 * The sidebar primary nav, grouped per the design: Platform (Inbox, Agents,
 * Sessions, Approvals) and Build (Skills, MCP servers, Files, Network
 * profiles, Gateway tools), with the preview-gated items in a third group.
 */
export function PrimaryNav(props: PrimaryNavProps): React.ReactElement {
  return (
    <nav className="app-nav" aria-label="Primary">
      <div className="nav-section">Platform</div>
      {/* fn-47.5 — Inbox carries the pending badge; React Query dedupes the
          underlying `approvals.pendingCount` poll across instances. */}
      {props.previewEnabled && (
        <NavLink to="/inbox" className="nav-item">
          <SbIcon name="comment" />
          <span className="label">Inbox</span> {props.pendingBadge}
        </NavLink>
      )}
      <NavLink to="/agents" className="nav-item">
        <SbIcon name="sparkles" />
        <span className="label">Agents</span>
      </NavLink>
      <NavLink to="/runs" className="nav-item">
        <SbIcon name="messages" />
        <span className="label">Sessions</span>
      </NavLink>
      <NavLink to="/approvals" className="nav-item">
        <SbIcon name="shield-check" />
        <span className="label">Approvals</span>
      </NavLink>
      <div className="nav-section">Build</div>
      <NavLink to="/skills" className="nav-item">
        <SbIcon name="magic-wand" />
        <span className="label">Skills</span>
      </NavLink>
      <NavLink to="/mcp-servers" className="nav-item">
        <SbIcon name="plug" />
        <span className="label">MCP servers</span>
      </NavLink>
      <NavLink to="/files" className="nav-item">
        <SbIcon name="folder" />
        <span className="label">Files</span>
      </NavLink>
      <NavLink to="/network-profiles" className="nav-item">
        <SbIcon name="shield-check" />
        <span className="label">Network profiles</span>
      </NavLink>
      <NavLink to="/gateway-tools" className="nav-item">
        <SbIcon name="grid" />
        <span className="label">Gateway tools</span>
      </NavLink>
      {/* Preview-only items (fn-72), all behind the same cosmetic gate. */}
      {props.previewEnabled && (
        <>
          <div className="nav-section">Preview</div>
          <NavLink to="/connectors" className="nav-item">
            <SbIcon name="plug" />
            <span className="label">Connectors</span>
          </NavLink>
          <NavLink to="/preview" className="nav-item">
            <SbIcon name="eye" />
            <span className="label">Preview</span>
          </NavLink>
          <NavLink to="/archie" className="nav-item">
            <SbIcon name="sparkles" />
            <span className="label">Archie</span>
          </NavLink>
        </>
      )}
    </nav>
  );
}

/**
 * Compact orange count pill on the Inbox nav item (the design's badge
 * treatment). Renders nothing at zero so the nav doesn't shift; a truncated
 * upstream count (`Infinity` from the hook) renders "many".
 */
function InboxCountBadge(): React.ReactElement | null {
  const count = usePendingApprovalsCount();
  if (typeof count !== 'number' || Number.isNaN(count) || count <= 0) return null;
  const label = Number.isFinite(count) ? `${count}` : 'many';
  return (
    <span className="nav-badge" aria-label={`${label} pending approvals`}>
      {label}
    </span>
  );
}

/**
 * The top-level app shell. Rendered by every authenticated route via
 * React Router's `Outlet`.
 */
export function AppLayout(props: AppLayoutProps): React.ReactElement {
  // The layout is always mounted under `<PreviewFeaturesProvider>` (main.tsx),
  // so it can read the hook directly and drive the pure `PrimaryNav`.
  const { enabled: previewEnabled } = usePreviewFeatures();
  return (
    <div className="app">
      <header className="header">
        <a className="header__logo" href="/" aria-label="Smokeball AI home">
          <img src={smokeballLogo} alt="Smokeball" />
          <span className="header__wordmark" aria-hidden="true">
            AI
          </span>
        </a>
        <div className="header__divider" />
        <div className="header__firm">Platform console</div>
        <div className="header__spacer" />
        <div className="header__cluster">
          <Link className="btn-create" to="/agents/new">
            <SbIcon name="sparkles" size={16} />
            Create New
          </Link>
        </div>
      </header>
      <aside className="sidebar" aria-label="Primary navigation">
        <PrimaryNav previewEnabled={previewEnabled} pendingBadge={<InboxCountBadge />} />
        <div className="sidebar__bottom">
          {/* fn-51 — the internal docs site (Astro Starlight) is served behind
              the same console Router at `/docs`, OUTSIDE the SPA. A plain
              router-external anchor, NOT a NavLink: clicking it top-level-
              navigates to the Astro-served `/docs/`. */}
          <a className="nav-item" href="/docs/">
            <SbIcon name="book-open-check" />
            <span className="label">Docs</span>
          </a>
          <UserMenu />
        </div>
      </aside>
      <main className="page">
        {props.banner !== undefined && (
          <output className="app-banner" aria-live="polite">
            {props.banner}
          </output>
        )}
        <Outlet />
      </main>
      {/* fn — "new version available" toast. Mounted once in the shell so
          it can surface on any authenticated route. Fixed bottom-right;
          renders nothing until a newer bundle is detected (and inert in
          dev — no changing `/version.json`). */}
      <VersionToast />
    </div>
  );
}
